import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { initIndex } from '../../../Callbacks/algoliaHelper/searchClient';
import fapResultsListFilters from '../../../config/FormConfigs/fapResultsListFilters';
import {
  appHomePage,
  initialFiltersState,
  facets,
  agentTypesMap,
  maxSelectedContractors,
  maxRadius,
} from '../../../config/fapApp.config';

import { FapAppContext } from '../../../context/fap-app.context';
import makeOptionsFromFacets from '../../../utils/makeOptionsFromFacets';
import {
  processCompanySearchParams,
  addSingleParam,
} from '../../../utils/processParams';
import redirectToPage from '../../../utils/redirectToPage';
import bem from '../../../utils/bem';
import { collectFilterQuery } from '../../../utils/algoliaFiltrationHelper';
import contractorPropsController from './contractorPropsController';
import makeLocationString from '../../../utils/makeLocationString';
import scrollTo from '../../../utils/scrollTo';

import ListingTemplate from '../../../Components/Templates/ListingTemplate';
import ContractorCard from '../../../Components/Molecules/Cards/ContractorCard';
import ContractorTray from '../../../Components/Organisms/ContractorTray';
import CTABanner from '../../../Components/Organisms/CTABanner';
import ContinueSearchContainer from './continueSearchContainer';
import LeadGenToolsPageHero from '../../../Components/Organisms/LeadGenToolsPageHero';
import {
  getZipFromGAddress,
  vendorResultsViewedEvent,
} from '../../../utils/analitic.utils';
import { useAnalyticsPushEvent } from '../../../analytic/analytic.context';
import { useLocation } from '../../../context/location.context';

const NoResultsResultsMsg = Drupal.t(
  'Please adjust your location radius or search for contractors in one of the selected product categories.',
);

function makeDefaultFacets(arr) {
  return arr.reduce((prev, curr) => {
    prev[curr.id] = 0;
    return prev;
  }, {});
}

function updateParamsWithContractors(filtersState, savedCategory, tray) {
  const selectedContractors = tray.map((el) => el.id);
  const processParams = addSingleParam({
    name: 'selectedContractors',
    value: selectedContractors.join(','),
  });
  const historySign = [
    { filters: filtersState, category: savedCategory, selectedContractors },
    '',
    `${window.location.pathname}?${processParams
      .toString()
      .replaceAll(/\+/g, '%20')}`,
  ];
  window.history.replaceState(...historySign);
}

function prepareDefaultFilters(savedLocation) {
  return {
    ...initialFiltersState,
    ...(savedLocation
      ? {
          location: makeLocationString(savedLocation),
        }
      : {}),
  };
}

function calculateScrollOffset() {
  const bodyPadding =
    parseInt(document.querySelector('body').style.paddingTop, 10) || 0;
  const offElements = ['.header', '.o-contractor-tray__title'];
  let offset = 0;

  for (let index = 0; index < offElements.length; index += 1) {
    const element = document.querySelector(offElements[index]);
    offset +=
      !element || window.innerWidth < 960
        ? 0
        : parseInt(element.offsetHeight, 10);
  }

  return bodyPadding + offset;
}

const FapResultsPage = () => {
  const {
    selectedCategory: savedCategory,
    selectedLocation: savedLocation,
    agentServices,
    qualifications,
    contractorsTray,
    commonSettings,
    algoliaSettings,
    setAgentType,
    setSelectedLocation,
    setSelectedCategory,
    setContractorsTray,
    setSavedFilters,
  } = useContext(FapAppContext);
  const { locationDetails } = useLocation();
  const pushAnalyticsEvent = useAnalyticsPushEvent();
  const listRef = useRef(null);
  if (!savedCategory) {
    redirectToPage(appHomePage);
    return null;
  }
  const filters = useCallback(
    collectFilterQuery({
      field_agent_type: agentTypesMap.contractor,
      field_agent_services: savedCategory?.value,
    }),
    [savedCategory],
  );

  const componentClassName = 'fap-results-page';
  const zip = getZipFromGAddress(savedLocation);
  const [loading, setLoading] = useState(false);
  const [wasSearched, setWasSearched] = useState(false);
  const [defaultFiltersState] = useState(prepareDefaultFilters(savedLocation));
  const [filtersState, setFiltersState] = useState(null);
  const [contractorTrayIsVisible, setContractorTrayVisible] = useState(null);
  const [results, setResult] = useState({
    page: 0,
    nbHits: 0,
    totalPages: 0,
    nbPages: 0,
    hitsPerPage: 8,
  });
  const [hits, setHits] = useState([]);
  const [filtersLists, setFiltersLists] = useState(fapResultsListFilters);
  const initialFacets = {
    field_agent_services: makeDefaultFacets(agentServices),
    certifications: makeDefaultFacets(qualifications),
  };
  let savedCategoryName;
  if (savedCategory.label === 'Residential Roofing') {
    savedCategoryName = 'company_location_roofing';
  } else if (savedCategory.label === 'Commercial Roofing') {
    savedCategoryName = 'company_location_commercial_roofing';
  } else if (savedCategory.label === 'Solar Roofing') {
    savedCategoryName = 'company_location_solar_roofing';
  } else if (savedCategory.label === 'Siding & Trim') {
    savedCategoryName = 'company_location_siding_and_trim';
  } else if (savedCategory.label === 'Insulation Products & Systems') {
    savedCategoryName = 'company_location_insulation';
  } else {
    savedCategoryName = 'company_location';
  }
  const savedCategoryIndex = algoliaSettings.replicas.find((r) =>
    r.endsWith(savedCategoryName),
  );
  const { algolia_index: defaultIndex, replicas } = algoliaSettings;
  const [sortByState, setSortByState] = useState(defaultIndex);
  const [index, setIndex] = useState(initIndex(savedCategoryIndex));
  const facetRefsMap = {
    field_agent_services: agentServices,
    certifications: qualifications,
  };
  const vendorDetailsExpandedAnalytics = {
    event: 'vendor_details_expanded',
    event_origin: 'front_end',
    product_category: savedCategory.label,
    vendor_type: 'contractor',
  };

  // This effect will try to set filters state if the page been loaded
  // by the direct URL with the companyLocation params set.
  useEffect(() => {
    const windowParams = new URL(document.location).searchParams;
    if (windowParams.get('agentType')) {
      setAgentType(windowParams.get('agentType'));
    }
    if (window.location.search.includes('companyLocation')) {
      const searchIndex =
        replicas.find((r) => r.includes(windowParams.get('sort'))) ||
        defaultIndex;
      setSortByState(searchIndex);
      setIndex(initIndex(searchIndex));
      setFiltersState({
        location: windowParams.get('aroundLatLng'),
        field_agent_services: JSON.parse(
          windowParams.get('facetFilters'),
        ).filter((e) => e.includes('field_agent_services')),
        radius: JSON.parse(windowParams.get('aroundRadius')),
        certifications: JSON.parse(windowParams.get('facetFilters')).filter(
          (e) => e.includes('certifications'),
        ),
        page: Number(windowParams.get('page')),
        hitsPerPage: Number(windowParams.get('hitsPerPage')),
      });
    } else {
      // Fallback to default filters is no URL params set on initial load.
      setFiltersState(defaultFiltersState);
    }
  }, []);

  useEffect(() => {
    const onPopStateHandler = (e) => {
      const { state } = e;
      if (Object.keys(state).length) {
        setFiltersState({ ...state.filters, fromHistory: true });
      } else {
        setFiltersState(defaultFiltersState);
      }
    };
    window.addEventListener('popstate', onPopStateHandler);

    return () => window.removeEventListener('popstate', onPopStateHandler);
  }, []);

  // Sync up location changes from Header to App
  useEffect(() => {
    if (locationDetails) {
      setSelectedLocation(locationDetails);
      setFiltersState((prev) => ({
        ...prev,
        location: makeLocationString(locationDetails),
        placeId: locationDetails[0]?.place_id,
      }));
    }
  }, [locationDetails]);

  useEffect(() => {
    if (hits.length === 0) {
      setFiltersState((prev) => ({
        ...prev,
        page: 0,
      }));
    }
  }, [hits.length]);

  useEffect(() => {
    // eslint-disable-next-line no-unused-vars
    let ignore = false;
    if (filtersState && savedCategory && savedLocation) {
      setLoading(true);
      const queryConfig = {
        page: filtersState?.page ? filtersState?.page : 0,
        hitsPerPage: filtersState?.hitsPerPage,
        facets,
        filters,
        facetFilters: [
          ...filtersState?.field_agent_services,
          ...filtersState?.certifications,
        ],
        aroundLatLng: filtersState?.location,
        aroundRadius: filtersState?.radius,
        attributesToRetrieve: [
          'nid',
          'title',
          'url',
          'field_number_of_reviews',
          'field_rating',
          'field_address',
          'field_mail',
          'field_phone',
          'field_site',
          'field_agent_services',
          'field_company_certifications',
          '_geoloc',
          'field_image',
        ],
      };
      index.search('', queryConfig).then((res) => {
        const { facets: responseFacets } = res;
        const resHits = res?.hits.map((h) => {
          const generalAnalyticsData = {
            event_origin: 'front_end',
            vendor_name: h.title,
            product_category: savedCategory.label,
            vendor_type: 'contractor',
            zip_code: zip,
            contentType: 'find_a_pro_listing',
            layout: 'null',
          };
          const analytics = {
            vendor_called: {
              event: 'vendor_called',
              ...generalAnalyticsData,
            },
            vendor_website_clicked: {
              event: 'vendor_website_clicked',
              ...generalAnalyticsData,
            },
          };
          const hit = contractorPropsController(
            h,
            agentServices,
            qualifications,
            savedLocation,
            savedCategory,
            zip,
            analytics,
          );
          return hit;
        });
        setResult(res);
        setHits(resHits);
        setFiltersLists((prev) =>
          prev.map((el) => {
            if (
              typeof responseFacets === 'object' &&
              Object.keys(responseFacets).length &&
              facets.includes(el.name)
            ) {
              el.options = makeOptionsFromFacets(
                responseFacets[el.name],
                facetRefsMap[el.name],
                savedCategory,
                initialFacets
                  ? initialFacets?.[el.name]
                  : responseFacets[el.name],
              );
            } else if (el.name !== 'field_geofield') {
              el.options = [];
            }
            return el;
          }),
        );
        if (wasSearched && !filtersState?.fromHistory) {
          const catName = encodeURIComponent(
            savedCategory.label.replaceAll(/&/g, 'and'),
          );
          const catVal = encodeURIComponent(
            JSON.stringify(savedCategory.value),
          );
          const sortVal =
            sortByState === defaultIndex
              ? 'default'
              : sortByState.split('_').pop();
          const processParams = processCompanySearchParams(
            `${res.params}&categoryName=${catName}&categoryValue=${catVal}&placeId=${savedLocation[0]?.place_id}&sort=${sortVal}&companyLocation=1`,
          );
          const historySign = [
            { filters: filtersState, category: savedCategory },
            '',
            `${window.location.pathname}?${processParams.toString()}`,
          ];
          window.history.pushState(...historySign);
          setSavedFilters(processParams.toString());
        }
        const analyticsData = vendorResultsViewedEvent({
          productCategory: savedCategory.label,
          contentType: 'find_a_pro',
          zipCode: zip,
          vendorType: 'contractor',
          numberOfResults: res.nbHits,
        });
        pushAnalyticsEvent(analyticsData);
        setLoading(false);
        setWasSearched(true);
      });
    }
    return () => {
      ignore = true;
    };
  }, [filtersState, savedCategory.label, savedLocation?.[0].place_id, index]);

  const clearCallback = () => {
    setFiltersState(defaultFiltersState);
  };

  const changePage = (newPage) => {
    setFiltersState((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const pagerData = {
    analyticData: {
      listingType: 'find_a_pro',
    },
    meta: {
      totalRows: results.nbHits,
      pageSize: results.hitsPerPage,
      totalPages: results.nbPages,
      pageNumber: results.page,
    },
    activePage: results.page,
    changePage,
    itemsPerPage: {
      settings: {
        name: 'itemsPerPage',
        label: Drupal.t('Items per page'),
        hideLabel: true,
        options: [
          {
            value: 8,
            label: `8 ${Drupal.t('results per page')}`,
          },
          {
            value: 12,
            label: `12 ${Drupal.t('results per page')}`,
          },
          {
            value: 16,
            label: `16 ${Drupal.t('results per page')}`,
          },
        ],
        state: { itemsPerPage: filtersState?.hitsPerPage },
      },
      onChange: (event) => {
        setFiltersState((prev) => ({
          ...prev,
          hitsPerPage: event.target.value,
        }));
      },
    },
  };

  const sortByOptions = useMemo(() => {
    return [
      {
        label: Drupal.t('Default'),
        value: defaultIndex,
      },
      {
        label: Drupal.t('Shortest distance'),
        value: replicas.find((e) => e.includes('nearest')),
      },
      {
        label: Drupal.t('Highest rated'),
        value: replicas.find((e) => e.includes('high_rating')),
      },
    ];
  }, []);

  const metaSettings = {
    itemsPerPage: results.hitsPerPage,
    totalResults: results.nbHits,
    sortByState,
    sortByOptions,
    setSortByState: (v) => {
      setIndex(initIndex(v));
      setSortByState(v);
    },
    showResults: false,
    bottomSort: true,
    showAppliedFilters: false,
    showFiltersText: 'More Filters',
    showFiltersAnalytic: () => {
      pushAnalyticsEvent({
        event: 'show_filter_clicked',
        event_origin: 'front_end',
        content_type: 'find_a_pro',
      });
    },
  };

  const listFilteredAnalyticsData = {
    event: 'list_filtered',
    event_origin: 'front_end',
    content_type: 'find_a_pro',
    zip_code: zip,
  };

  const resultsHeading = () => {
    const descStart = `${
      results.nbHits > 0
        ? `${results.nbHits} ${Drupal.t('Contractors found')}`
        : Drupal.t('No results found')
    } ${savedCategory ? `${Drupal.t('in')} ${savedCategory.label}` : ''}`;
    return {
      heading: results.nbHits ? Drupal.t('Results') : Drupal.t('No Results'),
      description: `${descStart} ${
        savedLocation
          ? `${Drupal.t('near')} ${savedLocation[0]?.formatted_address}`
          : ''
      }. ${!results.nbHits ? NoResultsResultsMsg : ''}`,
      descriptionMobile: `<h4>${descStart}</h4>${
        !results.nbHits
          ? `<p class="results-heading__mobile-p">${NoResultsResultsMsg}</p>`
          : ''
      }`,
    };
  };

  const ctaBannerProps = {
    headline: Drupal.t('We’re here to help'),
    description: commonSettings.no_results_text,
    cta: {
      link_url: commonSettings.no_results_button_url,
      link_content: commonSettings.no_results_button_label,
    },
    extra: ['listing__no-results-cta-banner'],
  };

  const onAddContractorHandler = (data) => {
    const oldTray = contractorsTray;
    const newTray = [...oldTray, data];
    setContractorsTray(newTray, agentServices, qualifications, savedLocation);
    updateParamsWithContractors(filtersState, savedCategory, newTray);
  };

  const onRemoveContractorHandler = (id) => {
    const oldTray = contractorsTray;
    if (oldTray.length) {
      const newTray = oldTray.filter((i) => i.id !== id);
      setContractorsTray(newTray, agentServices, qualifications, savedLocation);
      updateParamsWithContractors(filtersState, savedCategory, newTray);
    }
  };

  const onContinueSearchClickHandler = () => {
    setFiltersState((prev) => ({
      ...prev,
      radius: maxRadius,
    }));

    scrollTo(100, listRef.current.offsetTop - calculateScrollOffset());
  };

  const onResetSearchHandler = () => {
    setSelectedLocation(null);
    setSelectedCategory(null);
    setContractorsTray([]);
    redirectToPage(appHomePage);
  };

  return (
    <div
      className={bem(componentClassName, [
        contractorTrayIsVisible ? 'tray--is-visible' : '',
      ])}
    >
      <ContractorTray
        setTray={setContractorTrayVisible}
        contractors={contractorsTray}
        onRemove={(id) => onRemoveContractorHandler(id)}
        estimateLink={`${appHomePage}/get-an-estimate`}
        className={bem('contractor-tray', [], componentClassName, [])}
        offsetSelectors="#toolbar-bar, #toolbar-item-administration-tray, .header"
      />
      <div ref={listRef} className="container">
        <LeadGenToolsPageHero
          title={Drupal.t('Find a Pro Results')}
          description={commonSettings?.results_page_tooltip}
          bemBlock={componentClassName}
          onResetSearch={onResetSearchHandler}
        />
        <ListingTemplate
          metaSettings={results.hits && results.hits.length > 0 && metaSettings}
          filtersConfig={filtersLists}
          filtersClearAllBottom={true}
          filtersState={filtersState}
          setFiltersState={setFiltersState}
          setSelectedLocation={setSelectedLocation}
          clearAllCallback={clearCallback}
          pagerData={pagerData}
          resultsHeading={wasSearched && resultsHeading()}
          showSeparateLocation
          showSelectedSort
          mods={['fap-results']}
          defaultLocation={
            savedLocation?.[0]?.address_components?.[0]?.long_name
          }
          filtersAnalyticsData={listFilteredAnalyticsData}
        >
          {wasSearched && hits.length > 0 && (
            <ul className="fap-results-list">
              {hits.map((hit) => {
                return (
                  <li key={hit.id} className="fap-results-list__item">
                    <ContractorCard
                      contractorData={hit}
                      showCheckBox
                      showDefaultImage
                      onRemove={() => onRemoveContractorHandler(hit.id)}
                      onAdd={() => onAddContractorHandler(hit)}
                      isSelected={
                        contractorsTray &&
                        contractorsTray.find((el) => el.id === hit.id)
                      }
                      isButtonDisabled={
                        contractorsTray &&
                        contractorsTray.length >= maxSelectedContractors
                      }
                      checkBockTooltip={commonSettings.contractor_tray_tooltip}
                      analytics={{
                        vendorDetailsExpandedAnalytics,
                      }}
                    />
                  </li>
                );
              })}
            </ul>
          )}
          {!loading && wasSearched && !results.hits.length && (
            <>
              <CTABanner {...ctaBannerProps} />
              {filtersState.radius < maxRadius && (
                <ContinueSearchContainer
                  filtersState={filtersState}
                  index={index}
                  facets={facets}
                  maxRadius={maxRadius}
                  onCtaClick={() => onContinueSearchClickHandler()}
                />
              )}
            </>
          )}
        </ListingTemplate>
      </div>
    </div>
  );
};

export default FapResultsPage;
